import { useRef } from "react";
import { useSelector } from "react-redux";
import { selectSequenceVariables } from "../../../app/slices/TrainingSlice";
import VariableSuggestionPopper from "../../variables/VariableSuggestionPopper";
import VariableTextOverlay from "../../variables/VariableTextOverlay";
import useVariableTextareaController from "../../variables/useVariableTextareaController.hook";
import {
  hasFormattingTags,
  stripFormattingTags,
} from "../../rich_text/RichTextHtmlUtils";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";
import useTextareaAutosize from "./useTextareaAutosize.hook";

interface SequenceLocalizedVariableInputFieldProps {
  label: string;
  placeholder: string;
  localizationKey: string;
  allowVariables?: boolean;
  checkVariables?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  debounceTime?: number;
  minRows?: number;
  maxRows?: number;
  enableVariableIntellisense?: boolean;
}

export default function SequenceLocalizedVariableInputField({
  label,
  placeholder,
  localizationKey,
  allowVariables = true,
  checkVariables = true,
  disabled = false,
  autoFocus = false,
  debounceTime = 500,
  minRows = 2,
  maxRows = 6,
  enableVariableIntellisense = true,
}: SequenceLocalizedVariableInputFieldProps) {
  const { changeValue, getValue } = useTrainingLocalization();
  const variables = useSelector(selectSequenceVariables);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const storedValue = getValue(localizationKey);
  // This plain editor cannot render rich text; show the text without tags and
  // warn that editing here drops formatting. Commits only fire on user edits,
  // so untouched fields keep their formatting.
  const containsFormatting = hasFormattingTags(storedValue);
  const displayValue = containsFormatting
    ? stripFormattingTags(storedValue)
    : storedValue;
  const lineHeightPx = 20;
  const verticalPaddingPx = 16;
  const minHeightPx = Math.max(1, minRows) * lineHeightPx + verticalPaddingPx;
  const maxHeightPx =
    Math.max(Math.max(1, minRows), maxRows) * lineHeightPx + verticalPaddingPx;

  const {
    textareaRef,
    inputValue,
    warningText,
    highlightedSegments,
    scrollTop,
    scrollLeft,
    scrollbarWidth,
    handleFocus,
    handleChange,
    handleBlur,
    handleClick,
    handleKeyUp,
    handleScroll,
    handleWheel,
    handleKeyDown,
    suggestions,
  } = useVariableTextareaController({
    value: displayValue,
    onCommit: (nextValue) => changeValue(localizationKey, nextValue),
    variables,
    debounceMs: debounceTime,
    allowVariables,
    checkVariables,
    enableVariableIntellisense,
    flushOnCleanupKeys: [localizationKey],
  });

  useTextareaAutosize({
    textareaRef,
    value: inputValue,
    minHeightPx,
    maxHeightPx,
  });

  return (
    <div className="w-full">
      {label ? (
        <div className="mb-[6px] text-[14px] font-medium leading-[20px] text-creator-text-sub">
          {label}
        </div>
      ) : null}
      <div
        ref={inputWrapperRef}
        className={`relative h-auto w-full rounded-[6px] border border-solid bg-creator-surface transition-[border-color,box-shadow] duration-150 ${
          disabled
            ? "border-creator-border opacity-70"
            : "border-creator-border hover:border-creator-text-soft focus-within:border-creator-primary focus-within:shadow-[0_0_0_3px_rgba(58,196,255,0.15)]"
        }`}
      >
        <VariableTextOverlay
          segments={highlightedSegments}
          containerClassName="pointer-events-none absolute inset-0 overflow-hidden rounded-[5px]"
          contentClassName="min-h-full whitespace-pre-wrap break-words p-[8px] font-poppins text-[15px] leading-[20px]"
          textClassName="text-creator-primary"
          knownTokenClassName="text-creator-secondary"
          unknownTokenClassName="text-creator-error"
          scrollTop={scrollTop}
          scrollLeft={scrollLeft}
          scrollbarWidth={scrollbarWidth}
        />
        <textarea
          ref={textareaRef}
          className="nowheel relative z-10 block w-full resize-none bg-transparent p-[8px] font-poppins text-[15px] leading-[20px] text-transparent caret-creator-primary outline-none placeholder:text-creator-text-soft disabled:caret-transparent pit-creator-scrollbar"
          rows={minRows}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          style={{ minHeight: `${minHeightPx}px` }}
          onFocus={handleFocus}
          onChange={handleChange}
          onBlur={handleBlur}
          onClick={handleClick}
          onKeyUp={handleKeyUp}
          onScroll={(e) => {
            e.stopPropagation();
            handleScroll(e);
          }}
          onWheel={(e) => {
            e.stopPropagation();
            handleWheel(e);
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            handleKeyDown(e);
          }}
        />
      </div>
      {suggestions.open && (
        <VariableSuggestionPopper
          open={true}
          anchorEl={suggestions.anchorEl ?? inputWrapperRef.current}
          width={inputWrapperRef.current?.clientWidth}
          suggestions={suggestions.filteredSuggestions}
          selectedIndex={suggestions.selectedIndex}
          menuRef={suggestions.menuRef}
          onSelect={suggestions.onSelect}
        />
      )}
      {checkVariables && warningText && (
        <div className="mt-[4px] text-[12px] font-medium text-creator-error">
          {warningText}
        </div>
      )}
      {containsFormatting && (
        <div
          data-testid="sequence-localized-field-formatting-note"
          className="mt-[4px] text-[12px] font-medium text-warning-600"
        >
          This text has formatting; editing here removes it.
        </div>
      )}
    </div>
  );
}
